import { AssessmentCompletionConfig } from '../components/AssessmentCompletionTemplate';
import { computeAssessmentScore } from '../logic/scoring';

export const createSpiritualAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {
  // Handle both nested (assessmentData) and flat (direct) data structures
  let scores, results;

  if (assessmentResult.assessmentData) {
    scores = assessmentResult.assessmentData.scores;
    results = assessmentResult.assessmentData.results;
  } else if (assessmentResult.scores && assessmentResult.results) {
    scores = assessmentResult.scores;
    results = assessmentResult.results;
  } else {
    throw new Error('Invalid assessment result structure');
  }

  // Use unified scoring system
  const scoreResult = computeAssessmentScore(assessmentResult, assessmentResult.assessmentType);
  const scoreLabel = assessmentResult.assessmentType === 'fasdl' ? 'Spiritual Integration' : 'Spiritual Well-Being';

  // Add recommendations based on level if not already present
  if (!results.recommendations || results.recommendations.length === 0) {
    const level = results.level?.toLowerCase() || 'low';
    
    if (level === 'high') {
      results.recommendations = [
        'Continue nurturing your spiritual practices and inner peace',
        'Share your spiritual wisdom and strength with others',
        'Maintain the daily practices that support your spiritual growth',
        'Use your spiritual foundation as a source of resilience and meaning',
        'Consider exploring deeper spiritual development or mentoring others'
      ];
    } else if (level === 'moderate') {
      results.recommendations = [
        'Reflect on what spiritual practices bring you the most peace',
        'Dedicate time daily to spiritual activities that resonate with you',
        'Explore new spiritual practices that align with your values',
        'Connect with a spiritual community or mentor for support and growth',
        'Gradually deepen your spiritual practice through consistent engagement'
      ];
    } else {
      results.recommendations = [
        'Begin exploring spiritual practices that feel authentic to you',
        'Set aside time for quiet reflection, meditation, or prayer',
        'Consider reading spiritual texts or joining a spiritual community',
        'Speak with a spiritual guide, counselor, or mentor for direction',
        'Start small with simple daily practices to build spiritual connection'
      ];
    }
  }

  return {
    assessmentType: assessmentResult.assessmentType,
    assessmentName: 'spiritual-assessment',
    assessmentDisplayName: 'SPIRITUAL ASSESSMENT',
    redoRoute: '/panel/learn/spiritual-assessment',
    completeRoute: '/panel/insights',
    maxScore: scoreResult.max,
    scoreLabel,
    subtitleMapping: {
      High: 'High Spiritual Integration',
      Moderate: 'Developing',
      Low: 'Growing'
    },
    visualMessage: "You've completed the assessment!",
    resultsTitle: "Here's our evaluation",
    scores,
    results
  };
};

export const createNutritionalAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {
  const { scores, results } = assessmentResult;

  // Use unified scoring system
  const scoreResult = computeAssessmentScore(assessmentResult, 'nutritional');

  return {
    assessmentType: 'nutritional',
    assessmentName: 'nutritional-assessment',
    assessmentDisplayName: 'NUTRITIONAL ASSESSMENT',
    redoRoute: '/panel/learn/nutritional-assessment',
    completeRoute: '/panel/insights',
    maxScore: scoreResult.max,
    scoreLabel: 'Diet Quality',
    subtitleMapping: {
      High: 'Excellent',
      Moderate: 'Good',
      Low: 'Needs Improvement'
    },
    visualMessage: "You've completed the assessment!",
    resultsTitle: "Here's our evaluation",
    scores,
    results
  };
};

// Template for creating new assessment configurations
// DASS-21 subtitle mapping for completion page
export const DASS21_SUBTITLE_MAPPING = {
  Depression: [
    { label: 'Normal', range: '0–9', percent: '0% – 21.4%' },
    { label: 'Mild', range: '10–13', percent: '23.8% – 31.0%' },
    { label: 'Moderate', range: '14–20', percent: '33.3% – 47.6%' },
    { label: 'Severe', range: '21–27', percent: '50.0% – 64.3%' },
    { label: 'Extremely Severe', range: '28–42', percent: '66.7% – 100%' }
  ],
  Anxiety: [
    { label: 'Normal', range: '0–7', percent: '0% – 16.7%' },
    { label: 'Mild', range: '8–9', percent: '19.0% – 21.4%' },
    { label: 'Moderate', range: '10–14', percent: '23.8% – 33.3%' },
    { label: 'Severe', range: '15–19', percent: '35.7% – 45.2%' },
    { label: 'Extremely Severe', range: '20–42', percent: '47.6% – 100%' }
  ],
  Stress: [
    { label: 'Normal', range: '0–14', percent: '0% – 33.3%' },
    { label: 'Mild', range: '15–18', percent: '35.7% – 42.9%' },
    { label: 'Moderate', range: '19–25', percent: '45.2% – 59.5%' },
    { label: 'Severe', range: '26–33', percent: '61.9% – 78.6%' },
    { label: 'Extremely Severe', range: '34–42', percent: '81.0% – 100%' }
  ]
};
export const createAssessmentConfig = (
  assessmentResult: any,
  config: {
    assessmentType: string;
    assessmentName: string;
    assessmentDisplayName: string;
    redoRoute: string;
    completeRoute: string;
    maxScore?: number;
    scoreLabel: string;
    subtitleMapping: {
      High: string;
      Moderate: string;
      Low: string;
    };
    visualMessage?: string;
    resultsTitle?: string;
    selectedCategory?: number;
  }
): AssessmentCompletionConfig => {
  let { scores, results } = assessmentResult;

  // Reverse Employee Engagement scoring for display
  if (config.assessmentDisplayName?.toLowerCase().includes('employee engagement') && scores.engagementTotal !== undefined) {
    scores = {
      ...scores,
      engagementTotal: 5 - (scores.engagementTotal || 0)
    };
  }

  return {
    ...config,
    visualMessage: config.visualMessage || "You've completed the assessment!",
    resultsTitle: config.resultsTitle || "Here's our evaluation",
    scores,
    results: assessmentResult.results
  };
};

export const createGeneralPhysicalAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {
  // Handle both nested (assessmentData) and flat (direct) data structures
  let scores, results;

  if (assessmentResult.assessmentData) {
    scores = assessmentResult.assessmentData.scores;
    results = assessmentResult.assessmentData.results;
  } else if (assessmentResult.scores && assessmentResult.results) {
    scores = assessmentResult.scores;
    results = assessmentResult.results;
  } else {
    throw new Error('Invalid assessment result structure');
  }

  // Do not use scoring for this assessment - use personalized recommendations instead
  const scoreResult = { max: 100, level: 'none', levelIndex: 0 };

  // Extract health data for personalized recommendations
  const responses = assessmentResult.responses || [];
  let bmi: number | null = null;
  let bmiCategory = 'Unknown';
  let smoker = false;
  let chronicConditions: string[] = [];
  
  responses.forEach((response: any) => {
    const q = (response.question || '').toLowerCase();
    const answerLower = response.answer ? response.answer.toString().toLowerCase() : '';
    
    if (q.includes('smoker') || q.includes('tobacco')) {
      smoker = answerLower.includes('yes') || answerLower.includes('daily');
    }
    
    if (q.includes('chronic condition')) {
      // Only record chronic conditions if answer is "Yes" (not "No" or "None")
      if (response.answer && answerLower === 'yes') {
        chronicConditions = ['Yes'];
      } else if (response.answer && answerLower !== 'no' && answerLower !== 'none') {
        // If it's an array of specific conditions or a specific condition name
        chronicConditions = Array.isArray(response.answer) ? response.answer : [response.answer.toString()];
      }
    }
  });
  
  if (assessmentResult.physicalData) {
    const height = assessmentResult.physicalData.heightCm || assessmentResult.physicalData.height;
    const weight = assessmentResult.physicalData.weightKg || assessmentResult.physicalData.weight;
    if (height && weight) {
      const hm = height / 100;
      bmi = Math.round((weight / (hm * hm)) * 10) / 10;
      
      // Determine BMI category
      if (bmi < 18.5) {
        bmiCategory = 'Underweight';
      } else if (bmi < 25) {
        bmiCategory = 'Healthy Weight';
      } else if (bmi < 30) {
        bmiCategory = 'Overweight';
      } else {
        bmiCategory = 'Obese';
      }
    }
  }
  
  // Generate health status summary
  let healthStatus = 'Your overall physical health assessment is complete.';
  let hasRiskFactors = false;
  
  if (bmi) {
    if (bmi < 18.5 || bmi >= 25) {
      hasRiskFactors = true;
    }
  }
  
  if (smoker || chronicConditions.length > 0) {
    hasRiskFactors = true;
  }
  
  if (!hasRiskFactors && bmi && bmi >= 18.5 && bmi < 25) {
    healthStatus = 'You are in good physical health with a healthy BMI and no reported risk factors. Keep up your healthy lifestyle!';
  } else if (hasRiskFactors) {
    healthStatus = 'You have some health factors that need attention. The recommendations below will help guide you toward better health.';
  }
  
  const recommendations: string[] = [];
  
  // BMI Recommendations
  if (bmi) {
    if (bmi < 18.5) {
      recommendations.push(`Your BMI is ${bmi} (Underweight) - You may need to gain weight healthily. Eat nutrient-dense foods and consult your doctor.`);
      recommendations.push('Increase calorie intake with healthy foods like nuts, avocados, and whole grains.');
      recommendations.push('Include strength training to build muscle mass.');
    } else if (bmi < 25) {
      recommendations.push(`Your BMI is ${bmi} (Healthy Weight) - You are at a healthy weight. Continue maintaining these habits.`);
      recommendations.push('Keep up your current exercise routine and balanced diet.');
      recommendations.push('Regular physical activity helps maintain cardiovascular health.');
    } else if (bmi < 30) {
      recommendations.push(`Your BMI is ${bmi} (Overweight) - You may benefit from lifestyle modifications.`);
      recommendations.push('Aim for 150 minutes of moderate-intensity exercise per week.');
      recommendations.push('Focus on portion control and adding more vegetables and fruits to your diet.');
      recommendations.push('Consult a healthcare provider or nutritionist for personalized guidance.');
    } else {
      recommendations.push(`Your BMI is ${bmi} (Obese) - Your health may be at risk. Professional guidance is important.`);
      recommendations.push('Schedule an appointment with your healthcare provider to create a safe weight management plan.');
      recommendations.push('Start with gradual lifestyle changes: increase activity and improve diet quality.');
      recommendations.push('Consider working with a nutritionist or health coach for structured support.');
    }
  }
  
  // Smoking Recommendations
  if (smoker) {
    recommendations.push('Smoking significantly impacts your physical and cardiovascular health.');
    recommendations.push('Consider speaking with your doctor about smoking cessation programs or medications that can help.');
    recommendations.push('Explore resources like quit-smoking apps, support groups, or counseling services.');
  }
  
  // Chronic Conditions Recommendations
  if (chronicConditions.length > 0) {
    recommendations.push(`You reported having: ${chronicConditions.join(', ')}. Regular medical monitoring is essential.`);
    recommendations.push('Follow your healthcare provider\'s treatment plan and take medications as prescribed.');
    recommendations.push('Schedule regular follow-up appointments to monitor your condition.');
  }
  
  // Add general health recommendations if no specific issues
  if (!smoker && chronicConditions.length === 0 && bmi && bmi >= 18.5 && bmi < 25) {
    recommendations.push('Get 7-9 hours of quality sleep each night.');
    recommendations.push('Manage stress through meditation, yoga, or other relaxation techniques.');
    recommendations.push('Schedule regular health checkups with your doctor.');
  }
  
  results.recommendations = recommendations;
  results.bmi = bmi;
  results.bmiCategory = bmiCategory;
  results.healthStatus = healthStatus;

  return {
    assessmentType: 'physical-general',
    assessmentName: 'general-physical-assessment',
    assessmentDisplayName: 'GENERAL PHYSICAL ASSESSMENT',
    redoRoute: '/panel/learn/physical-wellness-assessment',
    completeRoute: '/panel/insights',
    maxScore: 100,
    scoreLabel: 'Assessment',
    hideScore: true,
    subtitleMapping: {
      High: healthStatus,
      Moderate: healthStatus,
      Low: healthStatus,
      Completed: healthStatus
    },
    visualMessage: "You've completed the General Physical Assessment!",
    resultsTitle: bmi ? `Your BMI: ${bmi} (${bmiCategory})` : "Your Physical Health Assessment",
    scores,
    results
  };
};

export const createIPAQSFAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {
  // Handle both nested (assessmentData) and flat (direct) data structures
  let scores, results;

  if (assessmentResult.assessmentData) {
    scores = assessmentResult.assessmentData.scores;
    results = assessmentResult.assessmentData.results;
  } else if (assessmentResult.scores && assessmentResult.results) {
    scores = assessmentResult.scores;
    results = assessmentResult.results;
  } else {
    throw new Error('Invalid assessment result structure');
  }

  // Use unified scoring system
  const scoreResult = computeAssessmentScore(assessmentResult, 'physical-ipaq');

  return {
    assessmentType: 'physical-ipaq',
    assessmentName: 'ipaq-sf-assessment',
    assessmentDisplayName: 'IPAQ-SF PHYSICAL ACTIVITY ASSESSMENT',
    redoRoute: '/panel/learn/physical-wellness-assessment',
    completeRoute: '/panel/insights',
    maxScore: scoreResult.max,
    scoreLabel: 'Physical Activity Score',
    subtitleMapping: {
      High: 'Excellent',
      Moderate: 'Good',
      Low: 'Needs Improvement'
    },
    visualMessage: "You've completed the Physical Activity Assessment!",
    resultsTitle: "Here's our evaluation of your physical activity levels",
    scores,
    results
  };
};

export const createPSQIAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {
  // Handle both nested (assessmentData) and flat (direct) data structures
  let scores, results;

  if (assessmentResult.assessmentData) {
    scores = assessmentResult.assessmentData.scores;
    results = assessmentResult.assessmentData.results;
  } else if (assessmentResult.scores && assessmentResult.results) {
    scores = assessmentResult.scores;
    results = assessmentResult.results;
  } else {
    throw new Error('Invalid assessment result structure');
  }

  // Use unified scoring system
  const scoreResult = computeAssessmentScore(assessmentResult, 'physical-psqi');

  return {
    assessmentType: 'physical-psqi',
    assessmentName: 'psqi-assessment',
    assessmentDisplayName: 'PSQI SLEEP QUALITY ASSESSMENT',
    redoRoute: '/panel/learn/physical-wellness-assessment',
    completeRoute: '/panel/insights',
    maxScore: scoreResult.max,
    scoreLabel: 'Sleep Score',
    subtitleMapping: {
      High: 'Good Sleep Quality',
      Moderate: 'Poor Sleep Quality',
      Low: 'Very Poor Sleep Quality'
    },
    visualMessage: "You've completed the Sleep Quality Assessment!",
    resultsTitle: "Here's our evaluation of your sleep quality",
    scores,
    results
  };
};

export const createPhysicalWellnessLearnAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {

  // Handle both nested (assessmentData) and flat (direct) data structures
  let scores, results;

  if (assessmentResult.assessmentData) {
    scores = assessmentResult.assessmentData.scores;
    results = assessmentResult.assessmentData.results;
  } else if (assessmentResult.scores && assessmentResult.results) {
    scores = assessmentResult.scores;
    results = assessmentResult.results;
  } else {
    throw new Error('Invalid assessment result structure');
  }

  // Map backend levels to frontend levels
  const mapLevel = (backendLevel: string): 'Low' | 'Moderate' | 'High' => {
    switch (backendLevel?.toLowerCase()) {
      case 'excellent':
        return 'High';
      case 'good':
        return 'High';
      case 'fair':
        return 'Moderate';
      case 'poor':
      case 'critical':
        return 'Low';
      default:
        return 'Moderate'; // Default fallback
    }
  };

  // Ensure results has the correct level format and property names
  const mappedResults = {
    level: mapLevel(results?.level),
    interpretation: results?.summary || results?.interpretation || 'Assessment completed successfully.'
  };

  // Use unified scoring system
  const scoreResult = computeAssessmentScore(assessmentResult, 'physical');

  return {
    assessmentType: 'physical',
    assessmentName: 'physical-wellness-assessment',
    assessmentDisplayName: 'PHYSICAL WELLNESS ASSESSMENT',
    redoRoute: '/panel/learn/physical-wellness-assessment',
    completeRoute: '/panel/insights',
    maxScore: scoreResult.max,
    scoreLabel: 'Wellness Score',
    subtitleMapping: {
      High: 'Excellent',
      Moderate: 'Good',
      Low: 'Needs Attention'
    },
    visualMessage: "You've completed the Physical Wellness Assessment!",
    resultsTitle: "Here's our evaluation of your physical wellness",
    scores: scores,
    results: mappedResults
  };
};

export const createFinancialAssessmentConfig = (
  assessmentResult: any
): AssessmentCompletionConfig => {
  const { scores, results, selectedCategory } = assessmentResult;

  // Debug logging for financial assessment




  // Use unified scoring system
  const scoreResult = computeAssessmentScore(assessmentResult, 'financial');

  return {
    assessmentType: 'financial',
    assessmentName: 'financial-assessment',
    assessmentDisplayName: 'FINANCIAL DISTRESS/WELL-BEING SCALE',
    redoRoute: '/panel/learn/financial-assessment',
    completeRoute: '/panel/insights',
    maxScore: scoreResult.max,
    scoreLabel: 'Financial Distress',
    subtitleMapping: {
      High: 'Low Financial Distress', // High score = Low Financial Distress
      Moderate: 'Moderate Financial Distress',
      Low: 'High Financial Distress' // Low score = High Financial Distress
    },
    visualMessage: "You've completed the assessment!",
    resultsTitle: "Here's your financial well-being analysis",
    scores,
    results,
    selectedCategory
  };
};


